import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  TextField, Typography, Stack, Paper, CircularProgress,
  MenuItem, Select, FormControl, IconButton, Tooltip, Divider, Chip
} from '@mui/material';
import { Search, EditTwoTone, CalendarMonth, Person, School } from '@mui/icons-material';
import TablePagination from '@mui/material/TablePagination';
import { collection, getDocs, query, where, doc, getDoc, setDoc, orderBy } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { db, auth } from "../../firebase";

const FONT_SIZE = '0.85rem';

function ClassAllocation() {
  const [schoolId, setSchoolId] = useState(null);
  const [academicYears, setAcademicYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);

  const [fetching, setFetching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);


  const [open, setOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [targetClassId, setTargetClassId] = useState(''); // Stores the ID

  // Helper to get Course Name from ID quickly
  const courseMap = useMemo(() => {
    const map = {};
    courses.forEach(c => { map[c.id] = c.name; }); // Adjust field name if it's 'name'
    return map;
  }, [courses]);

  const initSystem = async () => {
    try {
      const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
      if (userDoc.exists()) {
        const sId = userDoc.data().schoolId;
        setSchoolId(sId);

        // Fetch all years for the dropdown
        const yearSnap = await getDocs(query(
          collection(db, 'academicyears'),
          where('schoolId', '==', sId),
          where('status', "==", 'Active')
        ));

        const yearsData = yearSnap.docs.map(d => d.data());
        const yearNames = yearsData.map(y => y.name);
        setAcademicYears(yearNames);

        // AUTO-SELECT THE ACTIVE YEAR
        const activeYearObj = yearsData.find(y => y.status === 'Active');
        if (activeYearObj) {
          setSelectedYear(activeYearObj.name);
        } else if (yearNames.length > 0) {
          // Fallback to most recent if no "Active" found
          setSelectedYear(yearNames[0]);
        }
      }
    } catch (err) {
      console.error("Init Error:", err);
      toast.error("Failed to initialize system");
    }
  };

 const fetchData = async () => {
    if (!schoolId || !selectedYear) return;
    setFetching(true);
    try {
      // 1. Fetch Courses
      const cSnap = await getDocs(query(
        collection(db, 'courses'),
        where('schoolId', '==', schoolId),
        orderBy('sequence', 'asc')
      ));
      const fetchedCourses = cSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setCourses(fetchedCourses);

      // 2. Fetch Admissions
      const admSnap = await getDocs(query(
        collection(db, 'admission'),
        where('schoolId', '==', schoolId),
        where('academicYear', '==', selectedYear)
      ));
      const admissions = admSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      // 3. Fetch Enrollments
      const enrollSnap = await getDocs(query(
        collection(db, 'enrollments'),
        where('schoolId', '==', schoolId),
        where('academicYear', '==', selectedYear)
      ));

      const enrollmentMap = {};
      enrollSnap.forEach(d => {
        enrollmentMap[d.data().admissionId] = d.data().class;
      });

      // 4. Merge Data & Auto-Sync Class to Admission Collection
      const final = await Promise.all(admissions.map(async (adm) => {
        const sSnap = await getDoc(doc(db, 'students', adm.studentId));
        const s = sSnap.exists() ? sSnap.data() : {};
        
        const currentClassFromEnrollment = enrollmentMap[adm.id] || '';

        // AUTO-UPDATE: If admission record doesn't have the 'class' field 
        // but an enrollment exists, sync it back to the admission doc.
        if (currentClassFromEnrollment && adm.class !== currentClassFromEnrollment) {
            try {
                await setDoc(doc(db, 'admission', adm.id), {
                    class: currentClassFromEnrollment
                }, { merge: true });
            } catch (err) {
                console.error("Auto-sync failed for adm:", adm.id);
            }
        }

        return {
          ...adm,
          fullName: `${s.title || ''} ${s.firstname || ''} ${s.fathername || ''} ${s.surname || ''}`.trim().toUpperCase(),
          father_mobile1: adm.father_mobile1,
          currentClassId: currentClassFromEnrollment || adm.class || '' // Fallback to adm.class
        };
      }));

      setStudents(final);
    } catch (e) {
      console.error(e);
      toast.error("Sync Error");
    }
    setFetching(false);
  };

  useEffect(() => { initSystem(); }, []);
  useEffect(() => { fetchData(); }, [schoolId, selectedYear]);

  const handleSave = async () => {
    setLoading(true);
    try {
      const enrollmentId = `${selectedStudent.id}_${selectedYear.replace(/\s+/g, '')}`;
      const enrollRef = doc(db, 'enrollments', enrollmentId);

      await setDoc(enrollRef, {
        admissionId: selectedStudent.id,
        studentId: selectedStudent.studentId,
        schoolId: schoolId,
        academicYear: selectedYear,
        class: targetClassId, // Storing ID
        updatedAt: new Date().toISOString()
      }, { merge: true });

      toast.success(`Allocated to ${courseMap[targetClassId]}`);
      fetchData();
      setOpen(false);
    } catch (e) { toast.error("Allocation Failed"); }
    setLoading(false);
  };

  const filtered = useMemo(() =>
    students.filter(s => s.fullName?.toLowerCase().includes(search.toLowerCase())),
    [students, search]
  );

  return (
    <Box sx={{ bgcolor: '#f0f2f5', minHeight: '100vh', display: 'flex', flexDirection: 'column', p: 1 }}>

      {/* Search & Filter Header */}
      <Paper sx={{ p: 1, mb: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <School color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 800 }}>Class Allocation</Typography>
        </Stack>

        <Divider orientation="vertical" flexItem />

        <TextField
          placeholder="Search student..."
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ ml: 'auto', width: 250 }}
          InputProps={{ startAdornment: <Search sx={{ fontSize: 20, mr: 1 }} /> }}
        />
      </Paper>

      {/* Table Section */}
      <TableContainer component={Paper} sx={{ flexGrow: 1 }}>
        <Table size='small' stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 800, width: 60 }}>Sr.</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Student Name</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Contact</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Assigned Class</TableCell>
              <TableCell align="right" sx={{ fontWeight: 800 }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fetching ? (
              <TableRow><TableCell colSpan={5} align="center" sx={{ py: 10 }}><CircularProgress /></TableCell></TableRow>
            ) : filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((stu, index) => (
              <TableRow key={stu.id} hover sx={{ '& td': { fontSize: FONT_SIZE, py: 1.2 } }}>
                <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{stu.fullName}</TableCell>
                <TableCell>{stu.father_mobile1}</TableCell>
                <TableCell>
                  <Chip
                    label={courseMap[stu.currentClassId] || 'UNASSIGNED'}
                    size="small"
                    color={stu.currentClassId ? "success" : "error"}
                    variant="outlined"
                    sx={{ fontWeight: 800, fontSize: '0.7rem' }}
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSelectedStudent(stu);
                      setTargetClassId(stu.currentClassId);
                      setOpen(true);
                    }}
                  >
                    <EditTwoTone color="primary" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filtered.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, n) => setPage(n)}
        onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
      />

      {/* Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 800 }}>Allocate Class</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" gutterBottom>Student: {selectedStudent?.fullName}</Typography>
          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <Select
              value={targetClassId}
              onChange={(e) => setTargetClassId(e.target.value)}
              displayEmpty
            >
              <MenuItem value=""><em>-- Select Class --</em></MenuItem>
              {courses.map(c => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : "Update"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ClassAllocation;